import { Input, InputDate, Toogle } from 'alurkerja-ui'
import { useUiFromSpecContext } from '../../../EditorContextProvider'
import { isEmpty } from 'lodash'
import moment from 'moment'
import React, { useMemo, useCallback, useState, useEffect } from 'react'

type EditTooltipProps = {
  index: number
  element: string
  currentElementState: any
  setCurrentElementState: React.Dispatch<any>
}

export default function EditMinMaxDate(props: EditTooltipProps) {
  const { setDisableSaveEdit } = useUiFromSpecContext()
  const { index, element, currentElementState, setCurrentElementState } = props
  const [validationError, setValidationError] = useState<string | null>(null)

  // Initialize dateValidation state with defaults if not present
  useMemo(() => {
    if (!currentElementState.dateValidation) {
      setCurrentElementState((prevState: any) => ({
        ...prevState,
        dateValidation: {
          minDate: { fromField: false, isToday: false, value: null },
          maxDate: { fromField: false, isToday: false, value: null },
        },
      }))
    }
  }, [currentElementState, setCurrentElementState])

  const isDateTime = useMemo(() => {
    return currentElementState.form_field_type === 'INPUT_DATETIME-LOCAL'
  }, [currentElementState])

  // Get the minDate values with proper memoization
  const minDateValues = useMemo(
    () => ({
      isToday: currentElementState.dateValidation?.minDate?.isToday || false,
      fromField: currentElementState.dateValidation?.minDate?.fromField || false,
      value: currentElementState.dateValidation?.minDate?.value ?? null,
    }),
    [currentElementState.dateValidation?.minDate]
  )

  // Get the maxDate values with proper memoization
  const maxDateValues = useMemo(
    () => ({
      isToday: currentElementState.dateValidation?.maxDate?.isToday || false,
      fromField: currentElementState.dateValidation?.maxDate?.fromField || false,
      value: currentElementState.dateValidation?.maxDate?.value ?? null,
    }),
    [currentElementState.dateValidation?.maxDate]
  )

  // Validation function to check if dates are properly ordered
  const validateDates = useCallback(() => {
    // Clear previous validation error
    setValidationError(null)
    setDisableSaveEdit?.(false) // Disable save if max date is cleared

    // Only validate when both values are dates and neither are using fromField or isToday
    const hasMinDateValue = !minDateValues.fromField && !minDateValues.isToday && minDateValues.value instanceof Date
    const hasMaxDateValue = !maxDateValues.fromField && !maxDateValues.isToday && maxDateValues.value instanceof Date

    if (hasMinDateValue && hasMaxDateValue) {
      const minDate = new Date(minDateValues.value)
      const maxDate = new Date(maxDateValues.value)

      if (minDate > maxDate) {
        const dateType = isDateTime ? 'date time' : 'date'
        setValidationError(`Error: Min ${dateType} cannot be later than max ${dateType}`)
        setDisableSaveEdit?.(true) // Disable save if max date is cleared
      }
    }
  }, [minDateValues, maxDateValues, isDateTime])

  // Run validation when dates change
  useMemo(() => {
    validateDates()
  }, [minDateValues.value, maxDateValues.value, validateDates])

  // Handler for minDate isToday toggle
  const handleMinDateTodayToggle = useCallback(
    (value: string | number | boolean | undefined) => {
      // Convert to boolean
      const boolValue = Boolean(value)
      setCurrentElementState((prevState: any) => ({
        ...prevState,
        dateValidation: {
          ...prevState.dateValidation,
          minDate: {
            ...prevState.dateValidation?.minDate,
            isToday: boolValue,
            // If Today is enabled, disable fromField
            fromField: boolValue ? false : prevState.dateValidation?.minDate?.fromField,
            // Always set value to null
            value: null,
          },
        },
      }))
    },
    [setCurrentElementState]
  )

  // Handler for minDate fromField toggle
  const handleMinDateFromFieldToggle = useCallback(
    (value: string | number | boolean | undefined) => {
      // Convert to boolean
      const boolValue = Boolean(value)
      setCurrentElementState((prevState: any) => ({
        ...prevState,
        dateValidation: {
          ...prevState.dateValidation,
          minDate: {
            ...prevState.dateValidation?.minDate,
            fromField: boolValue,
            // If fromField is enabled, disable isToday
            isToday: boolValue ? false : prevState.dateValidation?.minDate?.isToday,
            // Always reset value to null when toggling
            value: null,
          },
        },
      }))
    },
    [setCurrentElementState]
  )

  // Handler for minDate value input change
  const handleMinDateValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setCurrentElementState((prevState: any) => ({
        ...prevState,
        dateValidation: {
          ...prevState.dateValidation,
          minDate: {
            ...prevState.dateValidation?.minDate,
            value: newValue,
          },
        },
      }))
    },
    [setCurrentElementState]
  )

  // Handler for minDate date picker change
  const handleMinDatePickerChange = useCallback(
    (date: Date | null | undefined) => {
      setCurrentElementState((prevState: any) => {
        // Clear validation error if we're removing the date
        if (!date) {
          setValidationError(null)
          setDisableSaveEdit?.(false) // Disable save if max date is cleared
        }

        return {
          ...prevState,
          dateValidation: {
            ...prevState.dateValidation,
            minDate: {
              ...prevState.dateValidation?.minDate,
              value: date || null,
            },
          },
        }
      })
    },
    [setCurrentElementState]
  )

  // Handler for maxDate isToday toggle
  const handleMaxDateTodayToggle = useCallback(
    (value: string | number | boolean | undefined) => {
      // Convert to boolean
      const boolValue = Boolean(value)
      setCurrentElementState((prevState: any) => ({
        ...prevState,
        dateValidation: {
          ...prevState.dateValidation,
          maxDate: {
            ...prevState.dateValidation?.maxDate,
            isToday: boolValue,
            // If Today is enabled, disable fromField
            fromField: boolValue ? false : prevState.dateValidation?.maxDate?.fromField,
            // Always set value to null
            value: null,
          },
        },
      }))
    },
    [setCurrentElementState]
  )

  // Handler for maxDate fromField toggle
  const handleMaxDateFromFieldToggle = useCallback(
    (value: string | number | boolean | undefined) => {
      // Convert to boolean
      const boolValue = Boolean(value)
      setCurrentElementState((prevState: any) => ({
        ...prevState,
        dateValidation: {
          ...prevState.dateValidation,
          maxDate: {
            ...prevState.dateValidation?.maxDate,
            fromField: boolValue,
            // If fromField is enabled, disable isToday
            isToday: boolValue ? false : prevState.dateValidation?.maxDate?.isToday,
            // Reset value to null on toggle change, unless fromField is true
            value: boolValue ? prevState.dateValidation?.maxDate?.value || null : null,
          },
        },
      }))
    },
    [setCurrentElementState]
  )

  // Handler for maxDate value input change
  const handleMaxDateValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setCurrentElementState((prevState: any) => ({
        ...prevState,
        dateValidation: {
          ...prevState.dateValidation,
          maxDate: {
            ...prevState.dateValidation?.maxDate,
            value: newValue,
          },
        },
      }))
    },
    [setCurrentElementState]
  )

  // Handler for maxDate date picker change
  const handleMaxDatePickerChange = useCallback(
    (date: Date | null | undefined) => {
      setCurrentElementState((prevState: any) => {
        // Clear validation error if we're removing the date
        if (!date) {
          setValidationError(null)
          setDisableSaveEdit?.(false) // Disable save if max date is cleared
        }

        return {
          ...prevState,
          dateValidation: {
            ...prevState.dateValidation,
            maxDate: {
              ...prevState.dateValidation?.maxDate,
              value: date || null,
            },
          },
        }
      })
    },
    [setCurrentElementState]
  )

  useEffect(() => {
    if (
      (currentElementState[element].minDate?.fromField && isEmpty(currentElementState[element].minDate?.value)) ||
      (currentElementState[element].maxDate?.fromField && isEmpty(currentElementState[element].maxDate?.value))
    ) {
      setValidationError('Please provide a field')
      setDisableSaveEdit?.(true) // Disable save if max date is cleared
    } else {
      setValidationError(null) // Clear validation error if conditions are met
      setDisableSaveEdit?.(false) // Enable save if conditions are met
    }
  }, [currentElementState])

  return React.useMemo(
    () => (
      <div className="flex flex-col gap-0 px-3 pt-2">
        <div key={index} className="pb-1 flex gap-2 rounded-sm">
          <span className="font-semibold">{isDateTime ? 'Date Time Validation' : 'Date Validation'}</span>
        </div>

        {/* Display validation error if there is one */}
        {validationError && <div className="mb-2 text-red-500 text-sm font-medium">{validationError}</div>}

        <div className="flex flex-col gap-1">
          {/* Min Date Section */}
          <div className="pb-1">
            <div className="font-medium text-sm mb-2">{isDateTime ? 'Min Date Time' : 'Min Date'}</div>
            <div className="flex items-center gap-2 mb-2">
              <Toogle id="todayMin" defaultValue={minDateValues.isToday} onChange={handleMinDateTodayToggle} />
              <label htmlFor="todayMin" className="select-none">
                Today
              </label>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <Toogle
                id="otherFieldMin"
                defaultValue={minDateValues.fromField}
                onChange={handleMinDateFromFieldToggle}
              />
              <label htmlFor="otherFieldMin" className="select-none">
                From Other Field
              </label>
            </div>

            {/* Show field input if fromField is true */}
            {minDateValues.fromField && (
              <Input
                className="w-full"
                placeholder={'${key_field}'}
                type="text"
                size="sm"
                rounded="lg"
                value={moment(minDateValues.value).isValid() ? null : minDateValues.value}
                onChange={handleMinDateValueChange}
              />
            )}

            {/* Show date picker if both toggles are false */}
            {!minDateValues.isToday && !minDateValues.fromField && (
              <div className="mt-2">
                <InputDate
                  onChange={handleMinDatePickerChange}
                  defaultValue={
                    typeof minDateValues.value === 'string' && minDateValues.value
                      ? moment(minDateValues.value).isValid()
                        ? moment(minDateValues.value).toDate()
                        : null
                      : maxDateValues.value
                  }
                  dateFormat={currentElementState?.['format'] ?? 'dd/MM/yyyy'}
                  size="sm"
                  timeFormat="HH:mm"
                  showTimeInput={isDateTime}
                  timeIntervals={5}
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  isClearable
                />
              </div>
            )}
          </div>

          {/* Max Date Section */}
          <div>
            <div className="font-medium text-sm mb-2">{isDateTime ? 'Max Date Time' : 'Max Date'}</div>
            <div className="flex items-center gap-2 mb-2">
              <Toogle id="todayMax" defaultValue={maxDateValues.isToday} onChange={handleMaxDateTodayToggle} />
              <label htmlFor="todayMax" className="select-none">
                Today
              </label>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <Toogle
                id="otherFieldMax"
                defaultValue={maxDateValues.fromField}
                onChange={handleMaxDateFromFieldToggle}
              />
              <label htmlFor="otherFieldMax" className="select-none">
                From Other Field
              </label>
            </div>

            {/* Show field input if fromField is true */}
            {maxDateValues.fromField && (
              <Input
                className="w-full"
                placeholder={'${key_field}'}
                type="text"
                size="sm"
                rounded="lg"
                value={moment(maxDateValues.value).isValid() ? null : maxDateValues.value}
                onChange={handleMaxDateValueChange}
              />
            )}

            {/* Show date picker if both toggles are false */}
            {!maxDateValues.isToday && !maxDateValues.fromField && (
              <InputDate
                onChange={handleMaxDatePickerChange}
                defaultValue={
                  typeof maxDateValues.value === 'string' && maxDateValues.value
                    ? moment(maxDateValues.value).isValid()
                      ? moment(maxDateValues.value).toDate()
                      : null
                    : maxDateValues.value
                }
                dateFormat={currentElementState?.['format'] ?? 'dd/MM/yyyy'}
                size="sm"
                timeFormat="HH:mm"
                showTimeInput={isDateTime}
                timeIntervals={5}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                isClearable
              />
            )}
          </div>
        </div>
      </div>
    ),
    [
      index,
      element,
      minDateValues,
      maxDateValues,
      isDateTime,
      validationError,
      handleMinDateTodayToggle,
      handleMinDateFromFieldToggle,
      handleMinDateValueChange,
      handleMinDatePickerChange,
      handleMaxDateTodayToggle,
      handleMaxDateFromFieldToggle,
      handleMaxDateValueChange,
      handleMaxDatePickerChange,
    ]
  )
}
